"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef,type HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/* ── Tag color tokens (semantic CSS variables) ── */
const TAG_COLOR_MAP = {
  green: {
    bg: "var(--color-element-sucess-light)",
    text: "var(--color-text-success)",
    border: "var(--color-border-sucess-light)",
  },
  orange: {
    bg: "var(--color-element-warning-light)",
    text: "var(--color-text-warning)",
    border: "var(--color-border-warning-light)",
  },
  red: {
    bg: "var(--color-element-danger-light)",
    text: "var(--color-text-danger)",
    border: "var(--color-border-danger-light)",
  },
  gray: {
    bg: "var(--color-element-gray-light)",
    text: "var(--color-text-gray)",
    border: "var(--color-border-secondary-light)",
  },
  blue: {
    bg: "var(--color-element-information-light)",
    text: "var(--color-text-information)",
    border: "var(--color-border-information-light)",
  },
  purple: {
    bg: "var(--color-element-hold-light)",
    text: "var(--color-text-hold)",
    border: "var(--color-border-hold-light)",
  },
  white: {
    bg: "var(--color-white)",
    text: "var(--color-text-gray)",
    border: "var(--color-border-secondary-light)",
  },
} as const;

type TagColor = keyof typeof TAG_COLOR_MAP;

/* ── Tag (small label, always outlined) ── */
export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  color?: TagColor;
}

const Tag = forwardRef<HTMLSpanElement, TagProps>(
  ({ color = "orange", className, children, style, ...props }, ref) => {
    const c = TAG_COLOR_MAP[color];
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center",
          "h-[18px] min-h-[18px] px-1.5",
          "text-[12px] font-[500] leading-[1.5]",
          "rounded-[4px] border",
          className,
        )}
        style={{
          backgroundColor: c.bg,
          color: c.text,
          borderColor: c.border,
          ...style,
        }}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Tag.displayName = "Tag";

/* ── Chip (larger badge with variant/size/shape) ── */
const chipVariants = cva(
  [
    "inline-flex items-center justify-center",
    "whitespace-nowrap",
    "text-[12px] font-[700] leading-[1.5]",
    "px-2",
  ],
  {
    variants: {
      size: {
        large: "h-7 min-h-7 min-w-[72px]",
        small: "h-6 min-h-6",
      },
      shape: {
        default: "rounded-[4px]",
        round: "rounded-full",
      },
    },
    defaultVariants: {
      size: "large",
      shape: "default",
    },
  }
);

export interface ChipProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  variant?: "solid" | "outline";
  color?: Exclude<TagColor, "white">;
}

const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ variant = "solid", size = "large", shape = "default", color = "green", className, children, style, ...props }, ref) => {
    const c = TAG_COLOR_MAP[color];
    const colorStyle =
      variant === "solid"
        ? { backgroundColor: c.bg, color: c.text }
        : {
            backgroundColor: "var(--color-element-white, #fff)",
            color: c.text,
            borderColor: c.border,
          };
    const borderWidth = variant === "outline" ? (size === "large" ? "2px" : "1px") : undefined;

    return (
      <span
        ref={ref}
        className={cn(
          chipVariants({ size, shape }),
          variant === "outline" && "border border-solid",
          className,
        )}
        style={{
          ...colorStyle,
          ...(borderWidth ? { borderWidth } : {}),
          ...style,
        }}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Chip.displayName = "Chip";

export { Chip,Tag };
export type { TagColor };
